import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Eyebrow} from '@/components/resumen-piezas'
import type {Etapa, Ruta} from '@/hooks/sgsst/resumen/use-ruta'
import {etapaHref} from '@/lib/ruta-nav'
import {cn} from '@/lib/utils'
import casimiroSaludo from '@/assets/casimiro/casimiro-saludo.webp'

// Más de tres ya es la Ruta completa, que está a un enlace.
const MAX_PASOS = 3

function PasoCard({etapa, numero, businessId}: {etapa: Etapa; numero: number; businessId: string}) {
  const {t} = useTranslation()
  const bloqueada = etapa.estado === 'bloqueada'
  return (
    <li
      className="flex flex-col gap-2 rounded-lg border border-border bg-secondary/40 p-4"
      data-testid={`arranque-paso-${etapa.clave}`}>
      <span className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-mono text-[11px] font-medium',
            bloqueada ? 'bg-muted text-muted-foreground' : 'bg-primary text-primary-foreground'
          )}>
          {numero}
        </span>
        <span className="text-sm font-semibold text-foreground">{t(`ruta.etapas.${etapa.clave}`)}</span>
      </span>
      <p className="text-[13px] leading-relaxed text-muted-foreground">{etapa.porque}</p>
      {bloqueada ? (
        <span className="mt-auto pt-1 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('home.arranque.bloqueada')}
        </span>
      ) : (
        <Button asChild size="sm" variant="outline" className="mt-auto w-fit">
          <Link to={`/businesses/${businessId}/${etapaHref(etapa.clave)}`}>{t(`ruta.cta.${etapa.clave}`)}</Link>
        </Button>
      )}
    </li>
  )
}

/** Varios módulos vacíos se funden acá en vez de apilarse. El orden sale de la Ruta
 * (`core/ruta.py`), no se inventa. */
export function EstadoDeArranque({
  ruta,
  businessId,
  bienvenida = false,
  className,
}: {
  ruta: Ruta
  businessId: string
  bienvenida?: boolean
  className?: string
}) {
  const {t} = useTranslation()
  const pasos = ruta.etapas
    .map((etapa, i) => ({etapa, numero: i + 1}))
    .filter(({etapa}) => etapa.estado !== 'hecha')
    .slice(0, MAX_PASOS)

  return (
    <section className={cn('surface p-5 sm:p-6', className)} data-testid="arranque">
      <div className="flex items-start justify-between gap-5">
        <div className="min-w-0">
          <Eyebrow>{t('home.arranque.eyebrow')}</Eyebrow>
          <h2 className="mt-2 font-heading text-lg text-foreground" data-testid="arranque-titulo">
            {bienvenida ? t('home.arranque.bienvenidaTitle') : t('home.arranque.title')}
          </h2>
        </div>
        {bienvenida && (
          <img
            src={casimiroSaludo}
            alt={t('home.arranque.casimiroAlt')}
            loading="lazy"
            decoding="async"
            className="hidden h-24 w-auto shrink-0 sm:block"
            data-testid="arranque-casimiro"
          />
        )}
      </div>

      {pasos.length > 0 ? (
        // `auto-fit`: la sección se monta a todo el ancho y también en dos tercios de una grilla.
        <ol className="mt-5 grid gap-3 sm:grid-cols-[repeat(auto-fit,minmax(15rem,1fr))]" data-testid="arranque-pasos">
          {pasos.map(({etapa, numero}) => (
            <PasoCard key={etapa.clave} etapa={etapa} numero={numero} businessId={businessId} />
          ))}
        </ol>
      ) : (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="arranque-al-dia">
          {t('home.arranque.alDia')}
        </p>
      )}

      <Link
        to={`/businesses/${businessId}/ruta`}
        className="mt-4 inline-block text-[13px] font-semibold text-primary hover:underline"
        data-testid="arranque-ver-ruta">
        {t('home.arranque.verRuta')} →
      </Link>
    </section>
  )
}
